import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum LabelPrinterModel {
  BROTHER_QL_1110NWB = 'brother_ql_1110nwb',
  ZEBRA_ZD230T = 'zebra_zd230t'
}

export enum LabelPrintMethod {
  /** The browser's print dialog; the printer driver sets the paper size. */
  PRINT_DIALOG = 'print_dialog',
  /** ZPL sent to the default printer of Zebra Browser Print, running on this computer. */
  ZEBRA_BROWSER_PRINT = 'zebra_browser_print'
}

/** Dots from the top-left of the label, as this printer is calibrated for it. */
type DotArea = { left: number; top: number; right: number; bottom: number };

/**
 * A printer and the labels loaded in it, as printed: width runs along the text. A Zebra keeps darkness, label
 * length and offsets saved in the printer (`^JUS`), so the labels never set them; recalibrating needs no code.
 */
export type LabelPrinter = { name: string; widthMm: number; heightMm: number } & (
  | { method: LabelPrintMethod.PRINT_DIALOG }
  | {
      method: LabelPrintMethod.ZEBRA_BROWSER_PRINT;
      dotsPerMm: number;
      /** Sent as `^PW` on every label: changing it shifts the whole print sideways. */
      headWidthDots: number;
      /** Where print lands on the label; content stays inside it. */
      printArea: DotArea;
    }
);

export type ZebraLabelPrinter = Extract<LabelPrinter, { method: LabelPrintMethod.ZEBRA_BROWSER_PRINT }>;

export const LABEL_PRINTERS = {
  [LabelPrinterModel.BROTHER_QL_1110NWB]: {
    name: 'Brother QL-1110NWB',
    widthMm: 100,
    heightMm: 62,
    method: LabelPrintMethod.PRINT_DIALOG
  },
  [LabelPrinterModel.ZEBRA_ZD230T]: {
    name: 'Zebra ZD230t',
    widthMm: 104,
    heightMm: 74,
    method: LabelPrintMethod.ZEBRA_BROWSER_PRINT,
    dotsPerMm: 8,
    headWidthDots: 832,
    printArea: { left: 24, top: 16, right: 808, bottom: 548 }
  }
} as const satisfies Record<LabelPrinterModel, LabelPrinter>;

interface LabelPrinterState {
  model: LabelPrinterModel;
  setModel: (model: LabelPrinterModel) => void;
}

/** The label printer connected to this computer; each computer picks its own. */
export const useLabelPrinterStore = create<LabelPrinterState>()(
  persist(
    (set) => ({
      model: LabelPrinterModel.BROTHER_QL_1110NWB,
      setModel: (model) => set({ model })
    }),
    { name: 'label-printer' }
  )
);

export const useLabelPrinter = (): LabelPrinter => LABEL_PRINTERS[useLabelPrinterStore((state) => state.model)];
